import { api } from "@/api"
import type { MaybeRef } from "@vueuse/core"
import { useQuery } from "@tanstack/vue-query"
import type { AnnotatedInvoice } from "../sharedTypes/invoices"
import type { TransactionDetail } from "../sharedTypes/transactions"

export type OrderDetailOrderItem = {
  id: string
  product: {
    id: string
    hasVariablePrice: boolean
    name: string
    color: string | null
  } | null
  productModifierLabels: Array<string> | null
  price: number
  note: string | null
}

type OrderDetail = {
  id: string
  note: string | null
  createdAt: Date
  totalCost: number
  cashierId: string
  invoice: AnnotatedInvoice | null
  orderItems: Array<OrderDetailOrderItem>
  payments: Array<TransactionDetail>
  orderNumber: number | null
}

type RetrieveOrderResponse = {
  order: RecursivelyConvertDatesToStrings<OrderDetail>
  receiptString: string
}

const parseOrderDetail = (
  order: RecursivelyConvertDatesToStrings<OrderDetail>
): OrderDetail => ({
  ...order,
  createdAt: DateFns.parseISO(order.createdAt),
  invoice: order.invoice && {
    ...order.invoice,
    createdAt: DateFns.parseISO(order.invoice.createdAt),
    paidAt: order.invoice.paidAt
      ? DateFns.parseISO(order.invoice.paidAt)
      : null,
  },
  payments: order.payments.map((payment) => ({
    ...payment,
    createdAt: DateFns.parseISO(payment.createdAt),
    refunds: payment.refunds.map((refund) => ({
      ...refund,
      createdAt: DateFns.parseISO(refund.createdAt),
    })),
  })),
})

export const retrieveOrder = async (orderId: string) => {
  const { body } = await api.get<RetrieveOrderResponse>(
    `/orders/${orderId}`
  )

  return {
    ...parseOrderDetail(body.order),
    receiptString: body.receiptString,
  }
}

export const useOrder = (orderId: MaybeRef<string>) =>
  useQuery(
    [BASE_ORDERS_QUERY_KEY, orderId],
    ({ queryKey: [_name, orderId_] }) =>
      retrieveOrder(unref(orderId_)),
    { refetchInterval: 60 * 1000 }
  )
